import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ProposalRendererV2 from '../components/ProposalRendererV2';
import ProposalEditorV2 from '../components/ProposalEditorV2';
import ClientSearch from '../components/ClientSearch';

const TONES = ['Professional', 'Warm & Friendly', 'Luxury'];
const LANGUAGES = ['Dutch', 'English'];

const DEFAULT_LAYOUT = { bg: '#ffffff', accent: 'var(--brand)', header: 'var(--brand)', text: '#1f2937' };
const BOARD_TYPES = ['', 'All Inclusive', 'Halfpension', 'Volpension', 'Logies & Ontbijt', 'Logies'];

function ExtractedCard({ data }) {
  if (!data) return null;
  const fields = [
    ['Bestemming', data.destination],
    ['Hotel', data.hotel_name && `${data.hotel_name}${data.hotel_stars ? ` (${data.hotel_stars}★)` : ''}`],
    ['Kamer', data.room_type],
    ['Vertrek', data.departure_airport || data.departure_city],
    ['Aankomst', data.arrival_city || data.arrival_airport],
    ['Datum', data.departure_date],
    ['Terugreis', data.return_date],
    ['Duur', data.duration_nights && `${data.duration_nights} nachten`],
    ['Prijs p.p.', data.price_per_person],
    ['Totaalprijs', data.price_total],
    ['Catering', data.board_type],
    ['Vlucht', data.flight_info],
  ].filter(([, v]) => v);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1">
        Geëxtraheerde reisdetails
      </p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {fields.map(([label, val]) => (
          <div key={label} className="bg-white rounded-lg px-3 py-2 border border-gray-200">
            <p className="text-xs text-gray-400">{label}</p>
            <p className="text-sm font-medium text-gray-800 truncate">{val}</p>
          </div>
        ))}
      </div>
      {data.key_highlights?.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-gray-600 mb-1">Hoogtepunten</p>
          {data.key_highlights.map((h, i) => <p key={i} className="text-xs text-gray-600">• {h}</p>)}
        </div>
      )}
      {data.inclusions?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">Inbegrepen</p>
          <div className="flex flex-wrap gap-1">
            {data.inclusions.map((inc, i) => <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">✓ {inc}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoLibraryModal({ onSelect, onClose, multiSelect = false, maxSelect = 3, onSelectMultiple }) {
  const { authFetch } = useAuth();
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]); // for multiSelect mode

  useEffect(() => {
    authFetch('/api/destination-photos').then(r => r.json()).then(d => {
      setHierarchy(d.hierarchy || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleMultiSelect = (url, label) => {
    setSelected(prev => {
      const exists = prev.find(p => p.url === url);
      if (exists) return prev.filter(p => p.url !== url);
      if (prev.length >= maxSelect) return prev;
      return [...prev, { url, label }];
    });
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8"><div className="spinner spinner-blue" style={{ width: 32, height: 32 }} /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">
            {multiSelect ? `Kies foto's (max ${maxSelect})` : 'Kies bestemmingsfoto'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {Object.entries(hierarchy).map(([continent, countries]) => (
            <div key={continent}>
              <button onClick={() => setExpanded(e => ({ ...e, [continent]: !e[continent] }))}
                className="w-full text-left px-3 py-2 rounded-lg font-semibold text-white text-sm"
                style={{ backgroundColor: 'var(--brand)' }}>
                {continent === 'Europa' ? '🌍' : '🌎'} {continent}
              </button>
              {expanded[continent] && Object.entries(countries).map(([country, areas]) => (
                <div key={country} className="ml-3 mt-1">
                  <p className="text-xs font-semibold text-gray-500 px-2 py-1 uppercase tracking-wide">{country}</p>
                  {Object.entries(areas).map(([area, photos]) => (
                    <div key={area} className="ml-2 mb-2">
                      <p className="text-xs text-gray-400 mb-1">📍 {area}</p>
                      <div className="flex gap-2 flex-wrap">
                        {photos.map(p => {
                          const isSelected = selected.some(s => s.url === p.photo_url);
                          return (
                            <button key={p.id}
                              onClick={() => {
                                if (multiSelect) { toggleMultiSelect(p.photo_url, `${area}, ${country}`); }
                                else { onSelect(p.photo_url, `${area}, ${country}`); onClose(); }
                              }}
                              className={`relative group rounded-lg overflow-hidden border-2 transition-all ${isSelected ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-blue-400'}`}
                              style={{ width: 100, height: 70 }}>
                              <img src={p.photo_url} alt={p.title} className="w-full h-full object-cover"
                                onError={e => { e.target.src = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=200&q=60'; }} />
                              {isSelected && (
                                <div className="absolute inset-0 bg-blue-500/30 flex items-center justify-center">
                                  <span className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                                    {selected.findIndex(s => s.url === p.photo_url) + 1}
                                  </span>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                              <p className="absolute bottom-0 left-0 right-0 text-white text-xs text-center py-1 bg-gradient-to-t from-black/60 opacity-0 group-hover:opacity-100 transition-all">{p.title}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
        {multiSelect && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">{selected.length} foto{selected.length !== 1 ? "'s" : ''} geselecteerd</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-secondary text-sm">Annuleren</button>
              <button onClick={() => { onSelectMultiple(selected); onClose(); }} disabled={selected.length === 0} className="btn-primary text-sm disabled:opacity-40">
                Bevestigen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- COMPARISON MODE COMPONENTS ----

function ComparisonOption({ idx, option, onUpdate, onRemove, canRemove }) {
  const { authFetch } = useAuth();
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState('');
  const pdfRef = useRef(null);

  const handleExtract = async () => {
    if (!option.url && !option.pdf_text) return;
    setExtracting(true); setExtractMsg('');
    try {
      const body = option.pdf_text ? JSON.stringify({ raw_text: option.pdf_text }) : JSON.stringify({ url: option.url });
      const res = await authFetch('/api/proposals/extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onUpdate(idx, 'extracted', data.extracted);
      setExtractMsg('Geanalyseerd!');
    } catch (e) {
      setExtractMsg('Fout: ' + e.message);
    }
    setExtracting(false);
  };

  const handlePdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('pdf', file);
    const res = await authFetch('/api/newsletter/extract-pdf', { method: 'POST', body: fd });
    const data = await res.json();
    if (data.text) { onUpdate(idx, 'pdf_text', data.text); onUpdate(idx, 'pdf_name', file.name); }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white animate-fade-in-up">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-gray-700 text-sm">Optie {idx + 1}</span>
        {canRemove && <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600 text-sm">× Verwijderen</button>}
      </div>
      <div className="space-y-3">
        <div>
          <label className="label">Naam van de optie</label>
          <input className="input text-sm" placeholder="bijv. All-inclusive Mallorca" value={option.title || ''}
            onChange={e => onUpdate(idx, 'title', e.target.value)} />
        </div>

        {/* URL + PDF */}
        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Via URL of PDF</p>
          <input className="input text-sm" placeholder="https://www.corendon.nl/..." value={option.url || ''}
            onChange={e => onUpdate(idx, 'url', e.target.value)} />
          <div className="flex items-center gap-2">
            <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={handlePdf} />
            <button onClick={() => pdfRef.current?.click()} className="btn-secondary text-xs py-1.5">PDF</button>
            {option.pdf_name && <span className="text-xs text-gray-500">{option.pdf_name}</span>}
            <button onClick={handleExtract} disabled={extracting || (!option.url && !option.pdf_text)}
              className="btn-secondary text-xs py-1.5 ml-auto">
              {extracting ? <span className="spinner" /> : null} {extracting ? 'Analyseren…' : 'Analyseer'}
            </button>
          </div>
          {extractMsg && <span className="text-xs text-green-600">{extractMsg}</span>}
          {option.extracted && <ExtractedCard data={option.extracted} />}
        </div>

        {/* Manual fields */}
        <div className="p-3 bg-blue-50 rounded-lg space-y-2">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Of handmatige invoer</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label text-xs">Bestemming</label>
              <input className="input text-sm" placeholder="Mallorca" value={option.destination || ''}
                onChange={e => onUpdate(idx, 'destination', e.target.value)} />
            </div>
            <div>
              <label className="label text-xs">Hotel</label>
              <input className="input text-sm" placeholder="Hotel Sol Beach" value={option.hotel_name || ''}
                onChange={e => onUpdate(idx, 'hotel_name', e.target.value)} />
            </div>
            <div>
              <label className="label text-xs">Nachten</label>
              <input type="number" className="input text-sm" placeholder="7" value={option.duration || ''}
                onChange={e => onUpdate(idx, 'duration', e.target.value)} />
            </div>
            <div>
              <label className="label text-xs">Prijs p.p. (€)</label>
              <input type="number" className="input text-sm" placeholder="899" value={option.price_per_person || ''}
                onChange={e => onUpdate(idx, 'price_per_person', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="label text-xs">Inbegrepen</label>
              <input className="input text-sm" placeholder="Vlucht, hotel, ontbijt" value={option.inclusions || ''}
                onChange={e => onUpdate(idx, 'inclusions', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="label text-xs">Hoogtepunten</label>
              <textarea className="input text-sm min-h-16" placeholder="Wat maakt deze optie bijzonder?" value={option.highlights || ''}
                onChange={e => onUpdate(idx, 'highlights', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Proposals() {
  const { authFetch, user } = useAuth();
  const [topTab, setTopTab] = useState('single'); // 'single' | 'compare'
  const [mode, setMode] = useState('manual');
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [form, setForm] = useState({
    url: '', destination: '', hotel_name: '', hotel_stars: '', room_type: '',
    duration: '', departure_city: '', departure_airport: '',
    departure_date: '', return_date: '', arrival_city: '',
    board_type: '', flight_info: '',
    price_per_person: '', price_total: '', inclusions: '', highlights: '',
    client_name: '', tone: 'Warm & Friendly', language: 'Dutch',
  });
  const [extractedData, setExtractedData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [proposalV2, setProposalV2] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [savedStatus, setSavedStatus] = useState(null); // null | 'saving' | 'saved' | 'unsaved'
  const [savedProposals, setSavedProposals] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [regenSection, setRegenSection] = useState(null); // section currently regenerating
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [urlAsLink, setUrlAsLink] = useState(false);
  const [pdfText, setPdfText] = useState('');
  const [pdfName, setPdfName] = useState('');
  const proposalRef = useRef(null);
  const pdfUploadRef = useRef(null);
  const proposalV2Ref = useRef(null);
  const [includeUrlInOutput, setIncludeUrlInOutput] = useState(false);

  // Comparison mode state
  const [compareOptions, setCompareOptions] = useState([
    { title: '', url: '', pdf_text: '', pdf_name: '', extracted: null, destination: '', hotel_name: '', duration: '', departure_city: '', price_per_person: '', inclusions: '', highlights: '' },
    { title: '', url: '', pdf_text: '', pdf_name: '', extracted: null, destination: '', hotel_name: '', duration: '', departure_city: '', price_per_person: '', inclusions: '', highlights: '' },
  ]);
  const [compareForm, setCompareForm] = useState({ tone: 'Warm & Friendly', language: 'Dutch' });
  const [comparing, setComparing] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCompareClient, setSelectedCompareClient] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [compareError, setCompareError] = useState('');

  const handleExtractUrl = async () => {
    if (!form.url && !pdfText) return;
    setExtracting(true); setExtractMsg(''); setExtractedData(null);
    try {
      const body = pdfText ? JSON.stringify({ raw_text: pdfText }) : JSON.stringify({ url: form.url });
      const res = await authFetch('/api/proposals/extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setExtractedData(data.extracted);
      // Pre-fill form from extracted data
      const ex = data.extracted;
      setForm(f => ({
        ...f,
        ...(ex.destination      && { destination:       ex.destination }),
        ...(ex.hotel_name       && { hotel_name:        ex.hotel_name }),
        ...(ex.hotel_stars      && { hotel_stars:       String(ex.hotel_stars) }),
        ...(ex.room_type        && { room_type:         ex.room_type }),
        ...(ex.duration_nights  && { duration:          String(ex.duration_nights) }),
        ...(ex.departure_date   && { departure_date:    ex.departure_date }),
        ...(ex.return_date      && { return_date:       ex.return_date }),
        ...(ex.departure_airport || ex.departure_city
          ? { departure_airport: ex.departure_airport || ex.departure_city,
              departure_city:    ex.departure_city || ex.departure_airport } : {}),
        ...(ex.arrival_city     && { arrival_city:      ex.arrival_city }),
        ...(ex.board_type       && { board_type:        ex.board_type }),
        ...(ex.flight_info      && { flight_info:       ex.flight_info }),
        ...(ex.price_per_person && { price_per_person:  ex.price_per_person.replace(/[^0-9.,]/g, '') }),
        ...(ex.price_total      && { price_total:       ex.price_total.replace(/[^0-9.,]/g, '') }),
        ...(ex.inclusions?.length && { inclusions: ex.inclusions.join(', ') }),
      }));
      setExtractMsg('Details geëxtraheerd!');
    } catch (e) {
      setExtractMsg('Fout: ' + e.message);
    }
    setExtracting(false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('pdf', file);
    const res = await authFetch('/api/newsletter/extract-pdf', { method: 'POST', body: fd });
    const data = await res.json();
    if (data.text) { setPdfText(data.text); setPdfName(file.name); }
  };

  // Load recent proposals on mount
  useEffect(() => {
    authFetch('/api/proposals').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setSavedProposals(d.slice(0, 10));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a saved proposal into the editor
  const loadProposal = async (id) => {
    try {
      const res = await authFetch(`/api/proposals/by-id/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const pv2 = data.proposal_data?.proposal_v2;
      if (!pv2) { alert('Dit voorstel heeft geen v2-indeling en kan niet worden bewerkt.'); return; }
      setProposalId(data.id);
      proposalV2Ref.current = pv2;
      setProposalV2(pv2);
      setResult({ shareToken: data.share_token, agencyName: pv2.branding_snapshot?.agency_name });
      setSavedStatus('saved');
      setShowEditor(false);
      setShowHistory(false);
      setError('');
    } catch (e) {
      alert('Laden mislukt: ' + e.message);
    }
  };

  // Save edited proposal_v2 to DB
  const handleSave = async () => {
    if (!proposalId || !proposalV2) return;
    setSavedStatus('saving');
    try {
      const res = await authFetch(`/api/proposals/${proposalId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ proposal_v2: proposalV2 }),
      });
      setSavedStatus(res.ok ? 'saved' : 'unsaved');
    } catch {
      setSavedStatus('unsaved');
    }
  };

  // Regenerate a single persuasive section
  const handleRegenerateSection = async (section) => {
    if (!proposalId || regenSection) return;
    setRegenSection(section);
    try {
      const res = await authFetch(`/api/proposals/${proposalId}/regenerate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ section, proposal_v2: proposalV2 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProposalV2(prev => ({
        ...prev,
        persuasive: {
          ...prev.persuasive,
          ...data.persuasive,
          ...(data.persuasive?.why_bullets && { why_bullets: data.persuasive.why_bullets.slice(0, 5) }),
        },
      }));
      setSavedStatus('unsaved');
    } catch (e) {
      alert('Regeneratie mislukt: ' + e.message);
    } finally {
      setRegenSection(null);
    }
  };

  const handleAiEdit = async (instruction) => {
    if (!proposalId || !instruction.trim()) return;
    try {
      const res = await authFetch(`/api/proposals/${proposalId}/ai-edit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ instruction, proposal_v2: proposalV2 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProposalV2(data.proposal_v2);
      setSavedStatus('unsaved');
    } catch (e) {
      throw e; // re-throw so the editor component can show the error
    }
  };

  // Track unsaved changes when editor modifies proposal_v2
  const handleProposalV2Change = (newV2) => {
    proposalV2Ref.current = newV2;
    setProposalV2(newV2);
    if (savedStatus !== 'saving') setSavedStatus('unsaved');
  };

  const handleGenerate = async () => {
    setError(''); setGenerating(true); setResult(null); setProposalV2(null); setProposalId(null); setSavedStatus(null); setShowEditor(false);
    try {
      const body = {
        mode,
        ...form,
        client_name: selectedClient ? selectedClient.full_name : (form.client_name || null),
        client_id: selectedClient?.client_id ?? null,
        extracted_data: extractedData ? JSON.stringify(extractedData) : null,
        pdf_text: pdfText || null,
        include_url_in_output: includeUrlInOutput,
      };
      const res = await authFetch('/api/proposals/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generatie mislukt');
      setResult(data);
      proposalV2Ref.current = data.proposal_v2;
      setProposalV2(data.proposal_v2);
      setProposalId(data.proposalId || null);
      setSavedStatus('saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCompare = async () => {
    setCompareError(''); setComparing(true); setCompareResult(null);
    try {
      // Enrich options with manual fields formatted as text if no extracted data
      const enrichedOptions = compareOptions.map(opt => {
        if (opt.extracted) return opt;
        const hasManual = opt.destination || opt.hotel_name || opt.price_per_person;
        if (!hasManual) return opt;
        const manualText = [
          opt.destination && `Bestemming: ${opt.destination}`,
          opt.hotel_name && `Hotel: ${opt.hotel_name}`,
          opt.duration && `Duur: ${opt.duration} nachten`,
          opt.departure_city && `Vertrek: ${opt.departure_city}`,
          opt.price_per_person && `Prijs p.p.: €${opt.price_per_person}`,
          opt.inclusions && `Inbegrepen: ${opt.inclusions}`,
          opt.highlights && `Hoogtepunten: ${opt.highlights}`,
        ].filter(Boolean).join('\n');
        return { ...opt, text: manualText };
      });
      const res = await authFetch('/api/proposals/compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          options: enrichedOptions,
          ...compareForm,
          client_name: selectedCompareClient ? selectedCompareClient.full_name : (compareForm.client_name || null),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Vergelijking mislukt');
      setCompareResult(data);
    } catch (err) {
      setCompareError(err.message);
    } finally {
      setComparing(false);
    }
  };

  const updateCompareOption = (idx, field, value) => {
    setCompareOptions(opts => opts.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  const addCompareOption = () => {
    if (compareOptions.length >= 6) return;
    setCompareOptions(opts => [...opts, { title: '', url: '', pdf_text: '', pdf_name: '', extracted: null, destination: '', hotel_name: '', duration: '', departure_city: '', price_per_person: '', inclusions: '', highlights: '' }]);
  };

  const removeCompareOption = (idx) => {
    setCompareOptions(opts => opts.filter((_, i) => i !== idx));
  };

  const copyShareLink = () => {
    if (!result?.shareToken) return;
    navigator.clipboard.writeText(`${window.location.origin}/proposal/${result.shareToken}`).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyForEmail = async () => {
    if (!proposalV2) return;
    const { travel_data: td, persuasive: p, branding_snapshot: bs, meta } = proposalV2;
    const brandColor = '#1e3a5f';

    const whyBulletsHtml = (p?.why_bullets || [])
      .map(b => `<div style="display:flex;gap:8px;margin:6px 0"><span style="color:${brandColor};font-weight:bold;flex-shrink:0">✓</span><span style="color:#374151">${b}</span></div>`)
      .join('');
    const inclusionsHtml = (p?.inclusions || [])
      .map(i => `<span style="display:inline-block;background:#f3f4f6;color:#374151;padding:3px 10px;border-radius:999px;font-size:12px;margin:3px 2px">✓ ${i}</span>`)
      .join('');

    const html = `<div style="font-family:sans-serif;max-width:580px;color:#111827">
      <h2 style="font-size:18px;font-weight:600;color:${brandColor};margin:0 0 4px">✈️ ${td?.destination || 'Reisvoorstel'}${td?.hotel_name ? ` — ${td.hotel_name}` : ''}</h2>
      ${p?.tagline ? `<p style="color:#6b7280;font-style:italic;margin:0 0 16px">"${p.tagline}"</p>` : ''}
      ${p?.greeting ? `<p style="margin:0 0 12px">${p.greeting}</p>` : ''}
      ${p?.why_intro ? `<p style="margin:0 0 8px">${p.why_intro}</p>` : ''}
      ${whyBulletsHtml ? `<p style="font-weight:600;margin:12px 0 4px">Waarom deze reis?</p>${whyBulletsHtml}` : ''}
      ${inclusionsHtml ? `<p style="font-weight:600;margin:12px 0 4px">Inbegrepen:</p><div style="margin:0 0 12px">${inclusionsHtml}</div>` : ''}
      <table style="border-collapse:collapse;margin:16px 0">
        ${td?.departure_date ? `<tr><td style="color:#9ca3af;padding:3px 12px 3px 0;font-size:13px">Vertrek</td><td style="font-size:13px">${td.departure_date}</td></tr>` : ''}
        ${td?.return_date ? `<tr><td style="color:#9ca3af;padding:3px 12px 3px 0;font-size:13px">Terug</td><td style="font-size:13px">${td.return_date}</td></tr>` : ''}
        ${td?.duration_nights ? `<tr><td style="color:#9ca3af;padding:3px 12px 3px 0;font-size:13px">Duur</td><td style="font-size:13px">${td.duration_nights} nachten</td></tr>` : ''}
        ${td?.departure_airport ? `<tr><td style="color:#9ca3af;padding:3px 12px 3px 0;font-size:13px">Luchthaven</td><td style="font-size:13px">${td.departure_airport}</td></tr>` : ''}
        ${td?.price_per_person ? `<tr><td style="color:#9ca3af;padding:3px 12px 3px 0;font-size:13px">Prijs p.p.</td><td style="font-size:16px;font-weight:700;color:${brandColor}">€${td.price_per_person}</td></tr>` : ''}
      </table>
      ${meta?.include_source_url && meta?.source_url ? `<p style="margin:0 0 16px"><a href="${meta.source_url}" style="display:inline-block;background:transparent;color:${brandColor};border:2px solid ${brandColor};padding:10px 20px;border-radius:6px;text-decoration:none;font-weight:600;font-size:14px">🔗 Bekijk het volledige aanbod online</a></p>` : ''}
      ${p?.closing ? `<p style="color:#6b7280;font-style:italic;margin:12px 0">${p.closing}</p>` : ''}
      <hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0">
      <p style="font-size:12px;color:#9ca3af;margin:0">${[bs?.agency_name, bs?.agent_name].filter(Boolean).join(' · ')}${bs?.phone ? ` &nbsp;📞 ${bs.phone}` : ''}${bs?.website_url ? ` &nbsp;🌐 ${bs.website_url}` : ''}</p>
    </div>`;

    const plainText = [
      `✈️ ${td?.destination || 'Reisvoorstel'}${td?.hotel_name ? ` — ${td.hotel_name}` : ''}`,
      p?.tagline ? `"${p.tagline}"` : '',
      '',
      p?.greeting || '',
      p?.why_intro || '',
      '',
      ...(p?.why_bullets?.length ? ['Waarom deze reis?', ...(p.why_bullets).map(b => `✓ ${b}`), ''] : []),
      ...(p?.inclusions?.length ? ['Inbegrepen:', ...(p.inclusions).map(i => `✓ ${i}`), ''] : []),
      td?.departure_date ? `Vertrek: ${td.departure_date}` : '',
      td?.return_date ? `Terug: ${td.return_date}` : '',
      td?.duration_nights ? `Duur: ${td.duration_nights} nachten` : '',
      td?.departure_airport ? `Luchthaven: ${td.departure_airport}` : '',
      td?.price_per_person ? `Prijs p.p.: €${td.price_per_person}` : '',
      meta?.include_source_url && meta?.source_url ? `Aanbod: ${meta.source_url}` : '',
      '',
      p?.closing || '',
      '',
      [bs?.agency_name, bs?.agent_name].filter(Boolean).join(' — '),
      bs?.phone ? `📞 ${bs.phone}` : '',
      bs?.website_url ? `🌐 ${bs.website_url}` : '',
    ].filter(l => l !== undefined && l !== false).join('\n');

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
    } catch {
      await navigator.clipboard.writeText(plainText);
    }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2500);
  };

  const downloadPDF = async () => {
    if (!proposalRef.current) return;
    try {
      const canvas = await html2canvas(proposalRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`voorstel-${form.destination || 'reis'}.pdf`);
    } catch (err) { alert('PDF export mislukt.'); }
  };

  const handleSendProposal = async () => {
    if (!sendEmail.trim() || !proposalId) return;
    setSending(true); setSendMsg('');
    try {
      // Always save first so the backend sends the latest version from DB
      if (proposalV2) {
        await authFetch(`/api/proposals/${proposalId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proposal_v2: proposalV2 }),
        });
        setSavedStatus('saved');
      }
      const res = await authFetch('/api/proposals/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: sendEmail.trim(), proposalId }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server fout (${res.status})`); }
      if (!res.ok) throw new Error(data?.error || `Server fout (${res.status})`);
      setSendMsg('Verstuurd!');
      setTimeout(() => { setSendModal(false); setSendMsg(''); setSendEmail(''); }, 2000);
    } catch (err) {
      setSendMsg('Fout: ' + err.message);
    }
    setSending(false);
  };

  const settings = result?.settings;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PageHeader title="Offertes" subtitle="Maak professionele reisvoorstellen met AI" />
      <div className="p-8" style={{ flex: 1, overflowY: 'auto' }}>

      {/* Top tabs: Single vs Compare */}
      <div className="flex border-b border-gray-200 mb-6">
        {[['single', 'Enkel voorstel'], ['compare', 'Vergelijkend voorstel']].map(([tab, label]) => (
          <button key={tab} onClick={() => setTopTab(tab)}
            className={topTab === tab
              ? 'px-4 py-2.5 text-sm font-medium border-b-2 text-gray-900 -mb-px'
              : 'px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700'}
            style={topTab === tab ? { borderColor: 'var(--brand)' } : {}}>
            {label}
          </button>
        ))}
      </div>

      {/* ===== SINGLE PROPOSAL ===== */}
      {topTab === 'single' && (
        <>
          {/* History panel */}
          {savedProposals.length > 0 && !proposalV2 && (
            <div className="pro-card mb-4">
              <button
                onClick={() => setShowHistory(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <span className="font-medium">Eerdere voorstellen</span>
                <span className="text-gray-400">{showHistory ? '▲' : '▼'}</span>
              </button>
              {showHistory && (
                <div className="border-t border-gray-100">
                  {savedProposals.map(sp => (
                    <div key={sp.id} className="flex items-center justify-between px-5 py-2.5 border-b border-gray-50 last:border-b-0">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{sp.destination || 'Onbekend'}</p>
                        <p className="text-xs text-gray-400">{new Date(sp.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                      <button onClick={() => loadProposal(sp.id)} className="btn-secondary text-xs py-1.5 px-3">
                        Laden
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="pro-card p-5 mb-6">
            {/* Mode tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              {[['manual', 'Handmatige invoer'], ['url', 'URL / PDF invoer']].map(([val, label]) => (
                <button key={val} onClick={() => setMode(val)}
                  className={mode === val
                    ? 'px-4 py-2.5 text-sm font-medium border-b-2 text-gray-900 -mb-px'
                    : 'px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700'}
                  style={mode === val ? { borderColor: 'var(--brand)' } : {}}>
                  {label}
                </button>
              ))}
            </div>

            {mode === 'url' ? (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="label">Pakket URL (Corendon, TUI, Booking.com, etc.)</label>
                  <input type="url" className="input" placeholder="https://www.corendon.nl/reis/..." value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
                  {form.url && (
                    <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={urlAsLink} onChange={e => setUrlAsLink(e.target.checked)} className="rounded" />
                      Voeg klikbare link toe in voorstel
                    </label>
                  )}
                </div>
                <div>
                  <label className="label">Of upload een PDF (reisbevestiging, offerte...)</label>
                  <div className="flex items-center gap-3">
                    <input ref={pdfUploadRef} type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} />
                    <button onClick={() => pdfUploadRef.current?.click()} className="btn-secondary text-sm">PDF selecteren</button>
                    {pdfName && <span className="text-sm text-gray-600 font-medium">{pdfName}</span>}
                  </div>
                </div>
                <div>
                  <button onClick={handleExtractUrl} disabled={extracting || (!form.url && !pdfText)}
                    className="btn-secondary flex items-center gap-2">
                    {extracting ? <span className="spinner" /> : null}
                    {extracting ? 'Details analyseren...' : 'Analyseer — extraheer reisdetails'}
                  </button>
                  {extractMsg && <p className="text-sm text-green-600 mt-1">{extractMsg}</p>}
                </div>
                {extractedData && <ExtractedCard data={extractedData} />}
              </div>
            ) : (
              <div className="space-y-4 mb-4">
                {/* URL reference box — always visible in manual mode */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="label text-xs text-blue-800">Pakket URL voor referentie (optioneel)</label>
                  <div className="flex gap-2 mt-1">
                    <input type="url" className="input flex-1 text-sm" placeholder="https://www.tui.nl/… of https://www.corendon.nl/…"
                      value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
                    {form.url && (
                      <button onClick={handleExtractUrl} disabled={extracting}
                        className="btn-secondary text-xs py-2 px-3 whitespace-nowrap">
                        {extracting ? <span className="spinner" /> : 'Analyseer'}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 mt-1">De AI gebruikt de URL als extra referentie naast uw handmatige invoer hieronder</p>
                  {extractMsg && <p className="text-xs text-green-600 mt-1">{extractMsg}</p>}
                  {extractedData && <div className="mt-2"><ExtractedCard data={extractedData} /></div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Bestemming *</label>
                    <input className="input" placeholder="bijv. Mallorca" value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Hotelnaam</label>
                    <input className="input" placeholder="bijv. Hotel Sol Beach" value={form.hotel_name} onChange={e => setForm({ ...form, hotel_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Vertrekdatum</label>
                    <input type="date" className="input" value={form.departure_date} onChange={e => setForm({ ...form, departure_date: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Terugkomstdatum</label>
                    <input type="date" className="input" value={form.return_date} onChange={e => setForm({ ...form, return_date: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Vertrekhaven / luchthaven</label>
                    <input className="input" placeholder="Amsterdam Schiphol (AMS)" value={form.departure_airport || form.departure_city} onChange={e => setForm({ ...form, departure_airport: e.target.value, departure_city: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Prijs per persoon (€)</label>
                    <input type="number" className="input" placeholder="899" value={form.price_per_person} onChange={e => setForm({ ...form, price_per_person: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Inbegrepen</label>
                    <input className="input" placeholder="Vlucht, hotel, transfer, ontbijt" value={form.inclusions} onChange={e => setForm({ ...form, inclusions: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Catering</label>
                    <select className="input" value={form.board_type} onChange={e => setForm({ ...form, board_type: e.target.value })}>
                      {BOARD_TYPES.map(t => <option key={t} value={t}>{t || '— kies type —'}</option>)}
                    </select>
                  </div>

                  {/* Collapsible extra fields */}
                  <div className="md:col-span-2">
                    <button type="button" onClick={() => setShowExtraFields(v => !v)}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-1">
                      {showExtraFields ? '▲' : '▼'} {showExtraFields ? 'Minder velden' : 'Meer reisdetails'}
                    </button>
                    {showExtraFields && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <label className="label text-xs">Aantal nachten</label>
                          <input type="number" className="input text-sm" placeholder="7" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} />
                        </div>
                        <div>
                          <label className="label text-xs">Aankomststad</label>
                          <input className="input text-sm" placeholder="Palma de Mallorca" value={form.arrival_city} onChange={e => setForm({ ...form, arrival_city: e.target.value })} />
                        </div>
                        <div>
                          <label className="label text-xs">Kamertype</label>
                          <input className="input text-sm" placeholder="Tweepersoonskamer zeezicht" value={form.room_type} onChange={e => setForm({ ...form, room_type: e.target.value })} />
                        </div>
                        <div>
                          <label className="label text-xs">Sterren hotel</label>
                          <input type="number" min="1" max="5" className="input text-sm" placeholder="4" value={form.hotel_stars} onChange={e => setForm({ ...form, hotel_stars: e.target.value })} />
                        </div>
                        <div>
                          <label className="label text-xs">Vluchtinfo</label>
                          <input className="input text-sm" placeholder="TUI 1234 — 09:30 → 13:15" value={form.flight_info} onChange={e => setForm({ ...form, flight_info: e.target.value })} />
                        </div>
                        <div>
                          <label className="label text-xs">Totaalprijs (€)</label>
                          <input type="number" className="input text-sm" placeholder="1799" value={form.price_total} onChange={e => setForm({ ...form, price_total: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="label">Extra highlights voor de AI</label>
                    <textarea className="input min-h-20" placeholder="Wat maakt deze reis bijzonder? De AI gebruikt dit voor de verkooptekst." value={form.highlights} onChange={e => setForm({ ...form, highlights: e.target.value })} />
                  </div>
                </div>
              </div>
            )}

            {/* Common fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
              <div>
                <label className="label">Klant <span className="font-normal text-gray-400">(optioneel)</span></label>
                <ClientSearch
                  selectedClient={selectedClient}
                  onSelect={setSelectedClient}
                  onClear={() => setSelectedClient(null)}
                />
              </div>
              <div>
                <label className="label">Toon</label>
                <select className="input" value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })}>
                  {TONES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Taal</label>
                <select className="input" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                  {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>

            {form.url && (
              <label className="flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={includeUrlInOutput} onChange={e => setIncludeUrlInOutput(e.target.checked)} className="rounded" />
                Voeg URL-inhoud toe aan voorstel
              </label>
            )}
            {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
            <div className="mt-5">
              <button onClick={handleGenerate} disabled={generating || (!form.destination && !form.url && !pdfText)} className="btn-primary">
                {generating ? <span className="spinner" /> : null}
                {generating ? 'Voorstel genereren...' : 'Voorstel genereren'}
              </button>
            </div>
          </div>

          {generating && !result && (
            <div className="pro-card p-5 text-center py-16">
              <div className="spinner spinner-blue mx-auto mb-4" style={{ width: 36, height: 36, borderWidth: 3 }} />
              <p className="text-gray-600 font-medium">Uw reisvoorstel wordt gegenereerd...</p>
              <p className="text-gray-400 text-sm mt-1">Dit kan 10-20 seconden duren</p>
            </div>
          )}

          {result && proposalV2 && (
            <>
              {/* Action bar */}
              <div className="flex gap-3 flex-wrap mb-5 items-center">
                <button onClick={copyShareLink} className="btn-primary">
                  {copied ? 'Gekopieerd!' : 'Kopieer deellink'}
                </button>
                <button onClick={copyForEmail} className="btn-secondary">
                  {emailCopied ? 'Gekopieerd!' : 'Kopieer voor e-mail'}
                </button>
                <button onClick={downloadPDF} className="btn-secondary">PDF</button>
                <button onClick={() => { setSendModal(true); setSendMsg(''); }} className="btn-secondary">
                  Verstuur per e-mail
                </button>

                {/* Save */}
                {proposalId && (
                  <button
                    onClick={handleSave}
                    disabled={savedStatus === 'saving' || savedStatus === 'saved'}
                    className={`btn-secondary flex items-center gap-1.5 ${
                      savedStatus === 'unsaved' ? 'border-amber-400 text-amber-700' : ''
                    }`}
                  >
                    {savedStatus === 'saving' && <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
                    {savedStatus === 'saved'   && <span className="text-green-600">✓</span>}
                    {savedStatus === 'saving'  ? 'Opslaan…' :
                     savedStatus === 'saved'   ? 'Opgeslagen' :
                     savedStatus === 'unsaved' ? 'Wijzigingen opslaan' : 'Opslaan'}
                  </button>
                )}

                <div className="ml-auto flex gap-2">
                  <button
                    onClick={() => setShowEditor(v => !v)}
                    className={`btn-secondary ${showEditor ? 'text-blue-700' : ''}`}
                  >
                    {showEditor ? 'Verberg editor' : 'Bewerken'}
                  </button>
                  <button
                    onClick={() => {
                      setResult(null); setProposalV2(null); setProposalId(null);
                      setSavedStatus(null); setExtractedData(null); setPdfText(''); setPdfName('');
                    }}
                    className="btn-secondary"
                  >
                    Nieuw voorstel
                  </button>
                </div>
              </div>

              {/* Editor + Renderer side-by-side on large screens */}
              <div className={showEditor ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start' : 'mb-6'}>
                {showEditor && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Bewerken</p>
                    <ProposalEditorV2
                      proposal_v2={proposalV2}
                      onChange={handleProposalV2Change}
                      onRegenerate={proposalId ? handleRegenerateSection : null}
                      regenSection={regenSection}
                      onAiEdit={proposalId ? handleAiEdit : null}
                    />
                  </div>
                )}
                <div ref={proposalRef}>
                  {showEditor && (
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Preview</p>
                  )}
                  <ProposalRendererV2 proposal_v2={proposalV2} agentSlug={user?.agent_slug} />
                </div>
              </div>

              {sendModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Verstuur reisvoorstel</h3>
                      <button onClick={() => setSendModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                    </div>
                    <label className="label">Ontvanger e-mailadres</label>
                    <input className="input mb-4" type="email" placeholder="klant@example.com" value={sendEmail}
                      onChange={e => setSendEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendProposal()} autoFocus />
                    {sendMsg && <p className={`text-sm mb-3 ${sendMsg.startsWith('Fout') ? 'text-red-600' : 'text-green-600'}`}>{sendMsg}</p>}
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setSendModal(false)} className="btn-secondary">Annuleren</button>
                      <button onClick={handleSendProposal} disabled={!sendEmail.trim() || sending} className="btn-primary disabled:opacity-40">
                        {sending ? <span className="spinner" /> : 'Versturen'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </>
      )}

      {/* ===== COMPARISON MODE ===== */}
      {topTab === 'compare' && (
        <div>
          <div className="pro-card p-5 mb-6">
            <p className="text-sm text-gray-600 mb-4">Voeg 2 tot 6 vakantieopties toe. Per optie kunt u een URL, PDF of handmatige invoer gebruiken. De AI maakt een vergelijkend voorstel voor uw klant.</p>
            <div className="space-y-4 mb-4">
              {compareOptions.map((opt, idx) => (
                <ComparisonOption key={idx} idx={idx} option={opt} onUpdate={updateCompareOption} onRemove={removeCompareOption} canRemove={compareOptions.length > 2} />
              ))}
            </div>
            {compareOptions.length < 6 && (
              <button onClick={addCompareOption} className="w-full py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all text-sm font-medium">
                + Optie toevoegen ({compareOptions.length}/6)
              </button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="label">Klant <span className="font-normal text-gray-400">(optioneel)</span></label>
                <ClientSearch
                  selectedClient={selectedCompareClient}
                  onSelect={setSelectedCompareClient}
                  onClear={() => setSelectedCompareClient(null)}
                />
              </div>
              <div>
                <label className="label">Toon</label>
                <select className="input" value={compareForm.tone} onChange={e => setCompareForm({ ...compareForm, tone: e.target.value })}>
                  {TONES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Taal</label>
                <select className="input" value={compareForm.language} onChange={e => setCompareForm({ ...compareForm, language: e.target.value })}>
                  {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
            {compareError && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{compareError}</div>}
            <div className="mt-4">
              <button onClick={handleCompare} disabled={comparing} className="btn-primary">
                {comparing ? <span className="spinner" /> : null}
                {comparing ? 'Vergelijking maken...' : 'Maak vergelijkend voorstel'}
              </button>
            </div>
          </div>

          {comparing && (
            <div className="pro-card p-5 text-center py-16">
              <div className="spinner spinner-blue mx-auto mb-4" style={{ width: 36, height: 36, borderWidth: 3 }} />
              <p className="text-gray-600 font-medium">Vakanties worden vergeleken...</p>
              <p className="text-gray-400 text-sm mt-1">Dit kan 20-30 seconden duren</p>
            </div>
          )}

          {compareResult && (() => {
            const lp = DEFAULT_LAYOUT;
            return (
              <div className="space-y-4 animate-fade-in-up rounded-lg overflow-hidden border border-gray-200">
                {/* Layout header bar */}
                <div className="px-6 py-4" style={{ backgroundColor: lp.header, color: '#fff' }}>
                  <p className="text-base font-bold leading-snug" style={{ color: '#fff' }}>{compareResult.comparison.intro}</p>
                </div>
                <div className="px-6 pb-2">
                  <div className={`grid grid-cols-1 md:grid-cols-${Math.min(compareResult.comparison.options?.length || 2, 2)} gap-4`}>
                    {(compareResult.comparison.options || []).map((opt, i) => (
                      <div key={i} className="rounded-lg border-2 p-4" style={{ borderColor: i === 0 ? lp.accent : i === 1 ? '#10b981' : i === 2 ? '#f59e0b' : '#8b5cf6', backgroundColor: lp.bg }}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold" style={{ color: lp.text }}>{opt.title || `Optie ${i + 1}`}</h3>
                          {opt.price_display && <span className="font-bold text-lg" style={{ color: lp.accent }}>{opt.price_display}</span>}
                        </div>
                        <p className="italic text-sm mb-3" style={{ color: lp.text, opacity: 0.7 }}>{opt.tagline}</p>
                        {opt.best_for && <p className="text-xs px-2 py-1 rounded inline-block mb-3" style={{ backgroundColor: lp.accent + '20', color: lp.accent }}>👥 {opt.best_for}</p>}
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-green-700 mb-1">Voordelen</p>
                          {(opt.pros || []).map((pro, j) => <p key={j} className="text-sm" style={{ color: lp.text }}>• {pro}</p>)}
                        </div>
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-amber-600 mb-1">Aandachtspunten</p>
                          {(opt.cons || []).map((con, j) => <p key={j} className="text-sm" style={{ color: lp.text }}>• {con}</p>)}
                        </div>
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs font-semibold mb-1" style={{ color: lp.accent }}>Verdict</p>
                          <p className="text-sm italic" style={{ color: lp.text }}>{opt.verdict}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mx-6 rounded-lg border-2 p-4" style={{ borderColor: lp.accent, backgroundColor: lp.accent + '15' }}>
                  <p className="text-sm font-bold mb-2" style={{ color: lp.accent }}>Onze aanbeveling</p>
                  <p className="leading-relaxed" style={{ color: lp.text }}>{compareResult.comparison.recommendation}</p>
                </div>
                <div className="px-6 pb-6">
                  <p className="italic text-sm" style={{ color: lp.text, opacity: 0.7 }}>{compareResult.comparison.closing}</p>
                </div>
                <div className="px-6 pb-6">
                  <button onClick={() => setCompareResult(null)} className="btn-secondary">Nieuwe vergelijking</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      </div>
    </div>
  );
}
